<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  // Agents that can be invited to a new private channel (current user excluded).
  agents: { type: Array, default: () => [] },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const dialogRef = ref(null);

const editing = ref(null);
const name = ref('');
const description = ref('');
const isPrivate = ref(false);
const memberIds = ref([]);
const readOnly = ref(false);
const announcement = ref('');
const search = ref('');

const isEdit = computed(() => !!editing.value);
const visibleAgents = computed(() => {
  const term = search.value.trim().toLowerCase();
  return props.agents.filter(agent =>
    (agent.available_name || agent.name || '').toLowerCase().includes(term)
  );
});

const open = (channel = null) => {
  editing.value = channel;
  name.value = channel?.name || '';
  description.value = channel?.description || '';
  isPrivate.value = channel?.private || false;
  memberIds.value = [];
  readOnly.value = channel?.read_only || false;
  announcement.value = channel?.announcement || '';
  search.value = '';
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const submit = () => {
  if (!name.value.trim()) return;
  emit('submit', {
    channel: editing.value,
    name: name.value.trim(),
    description: description.value.trim(),
    isPrivate: isPrivate.value,
    memberIds: isPrivate.value ? memberIds.value : [],
    readOnly: readOnly.value,
    announcement: announcement.value.trim(),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="isEdit ? t('TEAM_CHAT.EDIT') : t('TEAM_CHAT.CREATE_CHANNEL')"
    :confirm-button-label="isEdit ? t('TEAM_CHAT.SAVE') : t('TEAM_CHAT.CREATE')"
    :cancel-button-label="t('TEAM_CHAT.CANCEL')"
    :disable-confirm-button="!name.trim()"
    :is-loading="isSaving"
    width="md"
    overflow-y-auto
    @confirm="submit"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('TEAM_CHAT.CHANNEL_NAME')"
        :placeholder="t('TEAM_CHAT.CHANNEL_NAME_PLACEHOLDER')"
        @enter="submit"
      />
      <Input
        v-model="description"
        :label="t('TEAM_CHAT.CHANNEL_DESCRIPTION')"
        :placeholder="t('TEAM_CHAT.CHANNEL_DESCRIPTION_PLACEHOLDER')"
      />

      <div v-if="!isEdit" class="grid grid-cols-2 gap-2">
        <button
          v-for="option in [false, true]"
          :key="String(option)"
          type="button"
          class="flex flex-col items-start gap-1 p-3 text-start border rounded-xl"
          :class="
            isPrivate === option
              ? 'border-n-brand bg-n-blue-2'
              : 'border-n-weak hover:bg-n-alpha-1'
          "
          @click="isPrivate = option"
        >
          <span class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
            <span
              class="size-4"
              :class="option ? 'i-lucide-lock' : 'i-lucide-hash'"
            />
            {{ option ? t('TEAM_CHAT.PRIVATE') : t('TEAM_CHAT.PUBLIC') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ option ? t('TEAM_CHAT.PRIVATE_HINT') : t('TEAM_CHAT.PUBLIC_HINT') }}
          </span>
        </button>
      </div>

      <div v-if="!isEdit && isPrivate">
        <p class="mb-1.5 text-sm font-medium text-n-slate-12">
          {{ t('TEAM_CHAT.ADD_MEMBERS') }}
        </p>
        <label
          class="flex items-center gap-2 px-3 mb-1 rounded-lg h-9 bg-n-alpha-2 text-n-slate-10"
        >
          <span class="flex-shrink-0 size-4 i-lucide-search" />
          <input
            v-model="search"
            type="search"
            class="w-full !m-0 !p-0 text-sm !bg-transparent !border-0 !outline-none !shadow-none text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="t('TEAM_CHAT.SEARCH_PEOPLE')"
          />
        </label>
        <div class="overflow-y-auto max-h-48">
          <label
            v-for="agent in visibleAgents"
            :key="agent.id"
            class="flex items-center gap-2.5 px-1 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-1"
          >
            <input v-model="memberIds" type="checkbox" :value="agent.id" />
            <Avatar
              :src="agent.thumbnail"
              :name="agent.available_name || agent.name || '?'"
              :size="24"
              rounded-full
            />
            <span class="text-sm truncate text-n-slate-12">
              {{ agent.available_name || agent.name }}
            </span>
          </label>
        </div>
      </div>

      <template v-if="isEdit">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('TEAM_CHAT.ANNOUNCEMENT') }}
          </span>
          <textarea
            v-model="announcement"
            rows="2"
            class="!mb-0 text-sm resize-none rounded-lg"
            :placeholder="t('TEAM_CHAT.ANNOUNCEMENT_PLACEHOLDER')"
          />
        </label>
        <div class="flex items-center justify-between gap-3">
          <span>
            <span class="block text-sm font-medium text-n-slate-12">
              {{ t('TEAM_CHAT.READ_ONLY') }}
            </span>
            <span class="block text-xs text-n-slate-11">
              {{ t('TEAM_CHAT.READ_ONLY_HINT') }}
            </span>
          </span>
          <Switch v-model="readOnly" />
        </div>
      </template>
    </div>
  </Dialog>
</template>
